import { useQuery } from "@tanstack/react-query";
import { Tooltip, TooltipTrigger } from "@artis/ui";
import { dateFromMonthCode, genMonthCodeWithOffset, monthCodeFromOffset, type MonthCode } from "@package/grid-utils";
import cn from "mcn";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { type CalcResult, useCalcs } from "../../../calcs/react";

dayjs.extend(utc);

const isDependencyBlank = (err: string) => err.includes("missing-dependency");
const isDirectlyBlank = (err: string) => err.includes("No value found");

/**
 * Parses an expiry value coming from the calc engine. Different feeds encode
 * dates differently — most commonly YYYYMMDD as an 8-digit integer, but unix
 * timestamps (seconds or milliseconds) are also possible. Returns null when
 * the value isn't a recognisable date.
 */
const parseExpiry = (raw: number | null | undefined): Date | null => {
  if (raw == null || !Number.isFinite(raw)) return null;
  const n = Math.floor(raw);
  if (n >= 1900_01_01 && n <= 9999_12_31) {
    const y = Math.floor(n / 10_000);
    const m = Math.floor((n / 100) % 100);
    const d = n % 100;
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;
    return new Date(y, m - 1, d);
  }
  if (n > 1e12) return new Date(n);
  if (n > 1e9) return new Date(n * 1000);
  return null;
};

const contractMonthCodeForTraceRow = (row: number): MonthCode.MonthCodeYY | null => {
  const inRange = monthCodeFromOffset(row) as MonthCode.MonthCodeYY | undefined;
  const base = monthCodeFromOffset(0) as MonthCode.MonthCodeYY | undefined;
  if (inRange) return inRange;
  if (base) return genMonthCodeWithOffset(base, row);
  return null;
};

/**
 * When the calc store has no usable `expiry` cell (common for some feeds),
 * approximate last-trading for monthly ICE-style crude: end of the calendar
 * month **two months before** the contract month. Heuristic only — not
 * exchange-exact last trade day.
 */
const syntheticExpiryFromContractRow = (row: number): Date | null => {
  const code = contractMonthCodeForTraceRow(row);
  if (!code) return null;
  const iso = dateFromMonthCode(code);
  const contractStart = dayjs.utc(iso).startOf("month");
  if (!contractStart.isValid()) return null;
  return contractStart.subtract(2, "month").endOf("month").toDate();
};

/** Product IDs for which we fetch `expiry` and may show EXPIRED in Trace Precedents (e.g. BRT FT). */
const TRACE_PRECEDENTS_EXPIRY_PRODUCT_IDS = new Set(["cdc2ee85-7fa0-40a7-90bd-17b610aa4309"]);

type ValueDisplayProps = {
  label?: string;
  product: string;
  field: string;
  row: number;
  hideLabel?: boolean;
  /** When true, the row is beyond the configured grid horizon — treat as expired. */
  outOfGrid?: boolean;
};

export const ValueDisplay = ({ product, field, row, hideLabel, outOfGrid }: ValueDisplayProps) => {
  const calc = useCalcs();
  const useExpiryForProduct = TRACE_PRECEDENTS_EXPIRY_PRODUCT_IDS.has(product);
  const { data } = useQuery({
    queryKey: ["cell-value", product, field, row],
    refetchInterval: 250,
    queryFn: () => Promise.resolve(calc.getCell(product, field, row)),
  });
  const expiryQuery = useQuery({
    queryKey: ["cell-expiry", product, row],
    enabled: useExpiryForProduct,
    refetchInterval: 60_000,
    queryFn: () => Promise.resolve(calc.getCell(product, "expiry", row)),
  });
  const expiryRaw = expiryQuery.data?.Ok;
  const expiryDateFromEngine = parseExpiry(expiryRaw ?? undefined);
  const syntheticExpiry = useExpiryForProduct ? syntheticExpiryFromContractRow(row) : null;
  const now = Date.now();
  const hasEngineExpiry = expiryDateFromEngine != null;
  const isPastEngineExpiry = hasEngineExpiry && expiryDateFromEngine.getTime() < now;
  const expiryErr = expiryQuery.data?.Err;
  const noUsableEngineExpiry =
    expiryQuery.isFetched &&
    !hasEngineExpiry &&
    (expiryErr === undefined || isDirectlyBlank(expiryErr) || isDependencyBlank(expiryErr));
  const isPastSyntheticExpiry = noUsableEngineExpiry && syntheticExpiry != null && syntheticExpiry.getTime() < now;
  const isPastExpiry = isPastEngineExpiry || isPastSyntheticExpiry;

  const isExpired = data?.Ok == null && (outOfGrid || (useExpiryForProduct && isPastExpiry));
  const isMutedBlank = data?.Err !== undefined && isDependencyBlank(data.Err);

  return (
    <span className="inline-flex items-center gap-1 text-[10.5px]">
      {!hideLabel && <span className="text-[var(--neutral-default)]">{field}</span>}
      <span
        className={cn(
          "inline-flex justify-center items-center gap-1 px-2",
          "text-lg leading-[18px] font-normal rounded-lg border border-outline",
          isMutedBlank || isExpired ? "bg-[var(--colour-surface-disabled,var(--neutral-disabled))]" : "bg-card",
        )}
      >
        {isExpired ? <ExpiredChip outOfGrid={outOfGrid} /> : <ResultValue {...data} />}
      </span>
    </span>
  );
};

const ExpiredChip = ({ outOfGrid }: { outOfGrid?: boolean }) => (
  <TooltipTrigger as="span" touch="toggle" focusable>
    <span className="px-1.5 py-0.5 rounded text-[12px] text-[var(--colour-text-on-disabled,var(--neutral-on-disabled))]">
      EXPIRED
    </span>
    <Tooltip>
      {outOfGrid ? "This cell has expired and no longer has data" : "This contract has expired and no longer has data"}
    </Tooltip>
  </TooltipTrigger>
);

const BlankChip = ({ tooltip, muted }: { tooltip: string; muted: boolean }) => (
  <TooltipTrigger as="span" touch="toggle" focusable>
    <span
      className={cn("px-1.5 py-0.5 rounded text-[12px]", {
        "text-[var(--colour-text-on-disabled,var(--neutral-on-disabled))]": muted,
        "text-[var(--foreground-default)]": !muted,
      })}
    >
      BLANK
    </span>
    <Tooltip>{tooltip}</Tooltip>
  </TooltipTrigger>
);

const ResultValue = (value: Partial<CalcResult> | undefined) => {
  if (value?.Ok != null) {
    return (
      <span className="bg-[var(--success-tonal)] text-[var(--foreground-default)] px-1.5 py-0.5 rounded font-medium">
        {value.Ok.toFixed(2)}
      </span>
    );
  }
  if (value?.Err !== undefined) {
    if (isDependencyBlank(value.Err)) return <BlankChip tooltip="A precedent of this cell is missing data" muted />;
    if (isDirectlyBlank(value.Err)) return <BlankChip tooltip="This cell is currently empty" muted={false} />;
    return (
      <span className="bg-[var(--danger-tonal)] text-[var(--danger-on-tonal)] px-1.5 py-0.5 rounded text-[10px]">
        {value.Err}
      </span>
    );
  }
  return <BlankChip tooltip="This cell is currently empty" muted={false} />;
};
